// src/domUtil.ts
function el(selectors, parentNode = document) {
  const element = parentNode.querySelector(selectors);
  if (!element) throw new Error(`${selectors} must be a DOM element`);
  return element;
}
function bindQuerySelector(parentNode) {
  return (selectors) => el(selectors, parentNode);
}
function hide(element) {
  element.style.display = "none";
}
function show(element) {
  element.style.removeProperty("display");
}
function getVisible(element) {
  return element.style.display !== "none";
}
function setVisible(element, value) {
  if (getVisible(element) === value) return;
  if (value) show(element);
  else hide(element);
}
function isHidden(element) {
  let node = element;
  while (node) {
    if (getComputedStyle(node).display === "none") return true;
    node = node.parentElement;
  }
  return false;
}
function isInputElement(value) {
  return value?.tagName.toLowerCase() === "input";
}
function setClassToken(value, token, toggled) {
  if (toggled) {
    value.classList.add(token);
  } else {
    value.classList.remove(token);
  }
}
function walkDomDepthFirst(node, callback) {
  if (!node) return;
  let next = node.firstChild;
  while (next) {
    walkDomDepthFirst(next, callback);
    next = next.nextSibling;
  }
  callback(node);
}
function insertAfter(parent, newNode, referenceNode) {
  const next = referenceNode?.nextSibling ?? parent.firstElementChild;
  if (next === newNode) return;
  parent.insertBefore(newNode, next);
}
function insertBefore(parent, newNode, referenceNode) {
  if (referenceNode == null) {
    if (parent.lastElementChild === newNode) return;
    parent.appendChild(newNode);
    return;
  }
  if (referenceNode.previousSibling === newNode) return;
  parent.insertBefore(newNode, referenceNode);
}

// src/extensions/connectedObserver.ts
function initializeConnectedObserver() {
  const observer = new MutationObserver((mutations) => {
    const removed = /* @__PURE__ */ new Set();
    const added = /* @__PURE__ */ new Set();
    for (const mutation of mutations) {
      mutation.removedNodes.forEach((node) => removed.add(node));
      mutation.addedNodes.forEach((node) => added.add(node));
    }
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (removed.has(node)) continue;
        walkDomDepthFirst(node, (iNode) => {
          iNode.dispatchEvent(new CustomEvent("connect", { bubbles: false }));
        });
      }
      for (const node of mutation.removedNodes) {
        if (added.has(node)) continue;
        walkDomDepthFirst(node, (iNode) => {
          iNode.dispatchEvent(new CustomEvent("disconnect", { bubbles: false }));
        });
      }
    }
  });
  observer.observe(document, { childList: true, subtree: true });
  return () => {
    observer.disconnect();
  };
}
function onConnect(target, callback) {
  let sub = null;
  const onConnect2 = () => {
    sub = callback(target);
  };
  const onDisconnect = () => {
    sub?.();
    sub = null;
  };
  target.addEventListener("connect", onConnect2);
  target.addEventListener("disconnect", onDisconnect);
  if (target.isConnected) onConnect2();
  return () => {
    target.removeEventListener("connect", onConnect2);
    target.removeEventListener("disconnect", onDisconnect);
  };
}

// src/ActiveText.ts
function ActiveText(data) {
  const text = document.createTextNode(String(data.value));
  onConnect(text, () => {
    return data.onData((value) => {
      text.textContent = value == null ? null : String(value);
    });
  });
  return text;
}

// src/extensions/applyClassList.ts
import { isObservableValue } from "@amzn/vinyl-observable";
function applyClassList(element, classList) {
  const tokens = classList.filter((s) => typeof s === "string");
  element.classList.add(...tokens);
  if (tokens.length < classList.length) {
    onConnect(element, () => {
      const subs = [];
      for (const dP of classList.filter(isObservableValue)) {
        subs.push(bindClass(element, dP));
      }
      return () => {
        subs.forEach((sub) => sub());
      };
    });
  }
}
function bindClass(element, tokenProvider) {
  let current = null;
  const dataSub = tokenProvider.onData((className) => {
    if (current) element.classList.remove(current);
    if (className) {
      current = className;
      element.classList.add(className);
    }
  });
  return () => {
    if (current != null) element.classList.remove(current);
    dataSub();
  };
}

// src/stringUtil.ts
function toKebabCase(str) {
  return str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").replace(/[\s_]+/g, "-").replace(/[^a-zA-Z0-9-]/g, "").replace(/-+/g, "-").replace(/^-|-$/g, "").toLowerCase();
}

// src/extensions/applyStyles.ts
import { isObservableValue as isObservableValue2 } from "@amzn/vinyl-observable";
function applyStyles(element, style) {
  if (typeof style === "string") {
    throw new Error("expected style property to be type object");
  }
  for (const k in style) {
    const v = style[k];
    const property = toKebabCase(k);
    if (isObservableValue2(v)) {
      onConnect(element, () => {
        return v.onData((value) => {
          element.style.setProperty(property, value ?? null);
        });
      });
    } else {
      element.style.setProperty(property, v ?? null);
    }
  }
}

// src/extensions/applyVisibility.ts
function applyVisibility(element, visible) {
  if (typeof visible === "boolean") {
    if (!visible) element.style.display = "none";
  } else {
    onConnect(element, () => {
      return visible.onData((value) => {
        if (value) element.style.removeProperty("display");
        else element.style.display = "none";
      });
    });
  }
}

// src/objectUtil.ts
function notNull(value) {
  return value != null;
}

// src/jsx.ts
import {
  isObservableValue as isObservableValue3
} from "@amzn/vinyl-observable";

// src/extensions/applyPassiveHandler.ts
var passiveEventHandlers = {
  ontouchstart: applyPassiveHandler("ontouchstart"),
  ontouchmove: applyPassiveHandler("ontouchmove"),
  onwheel: applyPassiveHandler("onwheel"),
  onmousedown: applyPassiveHandler("onmousedown"),
  onmouseup: applyPassiveHandler("onmouseup")
};
function applyPassiveHandler(key) {
  const eventName = key.substring(2);
  return (element, handler) => {
    element.addEventListener(eventName, handler, { passive: true });
  };
}

// src/jsx.ts
var hookExtensions = {
  onConnect,
  style: applyStyles,
  visible: applyVisibility,
  classList: applyClassList,
  ...passiveEventHandlers
};
function jsx(type, props, ...children) {
  if (typeof type === "string") {
    const element = document.createElement(type);
    for (const k in props) {
      const v = props[k];
      if (k in hookExtensions) {
        hookExtensions[k](element, v);
        continue;
      }
      if (isObservableValue3(v)) {
        onConnect(element, () => {
          return v.onData((data$) => {
            if (data$ == null) element.removeAttribute(k);
            else element[k] = data$;
          });
        });
      } else {
        ;
        element[k] = v;
      }
    }
    element.append(
      ...children.filter(notNull).map((child) => {
        return isObservableValue3(child) ? ActiveText(child) : child;
      })
    );
    return element;
  } else {
    return type(props, children);
  }
}
function Fragment(_props, children) {
  return jsx(
    "div",
    {
      style: {
        display: "contents"
      }
    },
    ...children
  );
}
export {
  ActiveText,
  Fragment,
  applyClassList,
  applyStyles,
  applyVisibility,
  bindQuerySelector,
  el,
  getVisible,
  hide,
  hookExtensions,
  initializeConnectedObserver,
  insertAfter,
  insertBefore,
  isHidden,
  isInputElement,
  jsx,
  notNull,
  onConnect,
  setClassToken,
  setVisible,
  show,
  walkDomDepthFirst
};
